import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Skeleton,
  Alert,
  IconButton,
  Divider,
  Switch,
} from '@mui/joy';
import {
  ArrowLeft as BackIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  BellRinging as SubscribedIcon,
  BellSlash as UnsubscribedIcon,
  PaperPlaneTilt as SendIcon,
} from '@phosphor-icons/react';
import {
  useStormTerritory,
  useStormAlerts,
  useSubscribeToTerritory,
  useUnsubscribeFromTerritory,
  useDeleteStormTerritory,
} from '../../hooks/useStormTracker';
import { AlertsTable } from '../../components/stormtracker/AlertsTable';
import { TerritoryMap } from '../../components/stormtracker/TerritoryMap';
import { TerritoryZipMap } from '../../components/stormtracker/TerritoryZipMap';
import { TerritoryForm } from '../../components/stormtracker/TerritoryForm';
import { TestAlertModal } from '../../components/stormtracker/TestAlertModal';

const TerritoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const territoryId = id ? parseInt(id) : null;

  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isTestAlertOpen, setIsTestAlertOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: territory, isLoading, error, refetch } = useStormTerritory(territoryId);
  const { data: alerts, isLoading: alertsLoading } = useStormAlerts(
    territoryId ? { territory: territoryId, ordering: '-event_timestamp' } : undefined
  );

  const subscribeMutation = useSubscribeToTerritory();
  const unsubscribeMutation = useUnsubscribeFromTerritory();
  const deleteMutation = useDeleteStormTerritory();

  const windAlertCount = useMemo(
    () => (alerts || []).filter((a) => a.event_type === 'wind' || a.event_type === 'tornado').length,
    [alerts]
  );
  const hailAlertCount = useMemo(
    () => (alerts || []).filter((a) => a.event_type === 'hail').length,
    [alerts]
  );
  const recentAlerts = useMemo(() => (alerts || []).slice(0, 20), [alerts]);

  const handleSubscriptionToggle = async () => {
    if (!territoryId) return;

    setActionError('');
    try {
    if (territory?.is_subscribed) {
      await unsubscribeMutation.mutateAsync(territoryId);
    } else {
      await subscribeMutation.mutateAsync(territoryId);
    }
    void refetch();
    } catch { setActionError('Could not update your alert subscription. Please try again.'); }
  };

  const handleDelete = async () => {
    if (!territoryId || !window.confirm('Are you sure you want to delete this territory?')) return;

    setActionError('');
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(territoryId);
      navigate('/advanced/storm-tracker/territories');
    } catch {
      setActionError('Could not delete this watchlist. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditFormOpen(false);
    refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ mb: 2, borderRadius: 'md' }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} xs={6} md={3}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 'md' }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !territory) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Alert color="danger">Territory not found or failed to load.</Alert>
        <Button
          variant="outlined"
          startDecorator={<BackIcon />}
          onClick={() => navigate('/advanced/storm-tracker/territories')}
          sx={{ mt: 2 }}
        >
          Back to Territories
        </Button>
      </Box>
    );
  }

  // Old bookmarks must never reopen the retired manual-territory interface.
  if (!territory.is_auto_generated) {
    return <Navigate replace to="/advanced/storm-tracker/territories" />;
  }

  const getLocationSummary = () => {
    if (territory.zip_codes && territory.zip_codes.length > 0) {
      return `${territory.zip_codes.length} ZIP codes: ${territory.zip_codes.slice(0, 5).join(', ')}${territory.zip_codes.length > 5 ? '...' : ''}`;
    }
    if (territory.counties && territory.counties.length > 0) {
      return `${territory.counties.length} counties: ${territory.counties.join(', ')}`;
    }
    if (territory.radius_miles) {
      return `${territory.radius_miles} mile radius`;
    }
    return 'No area defined';
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {actionError && <Alert color="danger" sx={{ mb: 2 }}>{actionError}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography level="h2">{territory.name}</Typography>
            {territory.is_active ? (
              <Chip size="sm" color="success" variant="soft">Active</Chip>
            ) : (
              <Chip size="sm" color="neutral" variant="soft">Inactive</Chip>
            )}
          </Box>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            {getLocationSummary()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* Auto-generated watchlists always subscribe the owning rep in
              RepWatchlistService, so the Subscribe toggle is redundant. */}
          {!territory.is_auto_generated && (
            <Button
              variant={territory.is_subscribed ? 'soft' : 'outlined'}
              color={territory.is_subscribed ? 'success' : 'neutral'}
              startDecorator={territory.is_subscribed ? <SubscribedIcon /> : <UnsubscribedIcon />}
              onClick={handleSubscriptionToggle}
              loading={subscribeMutation.isPending || unsubscribeMutation.isPending}
            >
              {territory.is_subscribed ? 'Subscribed' : 'Subscribe'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<SendIcon />}
            onClick={() => setIsTestAlertOpen(true)}
          >
            Test Alert
          </Button>
          <IconButton variant="outlined" color="neutral" onClick={() => setIsEditFormOpen(true)}>
            <EditIcon />
          </IconButton>
          {!territory.is_auto_generated && (
            <IconButton variant="outlined" color="danger" onClick={handleDelete} loading={isDeleting}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid xs={12} md={5}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography level="h3" sx={{ color: 'var(--brand-text, #7013f5)' }}>
                  {windAlertCount}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Recent Wind Alerts
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography level="h3" sx={{ color: 'warning.500' }}>
                  {hailAlertCount}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Recent Hail Alerts
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Card variant="outlined">
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography level="title-md">Recent Alerts</Typography>
            </Box>
            <AlertsTable
              alerts={recentAlerts}
              isLoading={alertsLoading}
              showTerritory={false}
              emptyMessage="No alerts yet"
            />
          </Card>
        </Grid>

        <Grid xs={12} md={7}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            {territory.is_auto_generated || (territory.zip_codes && territory.zip_codes.length > 0 && !territory.center_latitude) ? (
              <TerritoryZipMap zipCodes={territory.zip_codes || []} height={350} />
            ) : (
              <TerritoryMap territory={territory} height={350} />
            )}
          </Card>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography level="h3">{territory.min_hail_size_inches}"</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Min Hail Size
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography level="h3">{territory.min_wind_speed_mph} mph</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Min Wind Speed
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Card variant="outlined">
            <Box sx={{ p: 2 }}>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Notification Settings
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography level="body-md">SMS Notifications</Typography>
                    <Switch checked={territory.notify_via_sms} disabled />
                  </Box>
                </Grid>
                <Grid xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography level="body-md">Push Notifications</Typography>
                    <Switch checked={territory.notify_via_push} disabled />
                  </Box>
                </Grid>
                {territory.quiet_hours_start && territory.quiet_hours_end && (
                  <Grid xs={12}>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Quiet Hours
                    </Typography>
                    <Typography level="body-lg">
                      {territory.quiet_hours_start} - {territory.quiet_hours_end}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Button variant="outlined" color="primary" onClick={() => setIsEditFormOpen(true)}>
                Edit Settings
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <TerritoryForm
        open={isEditFormOpen}
        onClose={() => setIsEditFormOpen(false)}
        onSuccess={handleEditSuccess}
        territory={territory}
      />

      <TestAlertModal
        open={isTestAlertOpen}
        onClose={() => setIsTestAlertOpen(false)}
        territoryId={territory.id}
        territoryName={territory.name}
      />
    </Box>
  );
};

export default TerritoryDetail;
